import { useCallback, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useAuth, useToast } from '@/shared/contexts';
import { AddCategoryModal } from './Components/AddCategoryModal';
import { AddItemsForm, draftToListedItem } from './Components/AddItemsForm';
import { ImportStep } from './Components/ImportStep';
import { OnboardingSidebar } from './Components/OnboardingSidebar';
import { OutOfNetworkSupplierModal } from './Components/OutOfNetworkSupplierModal';
import { ParLevelsStep } from './Components/ParLevelsStep';
import { RestaurantStep } from './Components/RestaurantStep';
import { ReviewStep } from './Components/ReviewStep';
import { StorageStep } from './Components/StorageStep';
import { SuppliersStep } from './Components/SuppliersStep';
import {
  bulkAssignRestaurantInventorySupplier,
  bulkCreateRestaurantInventoryItems,
  bulkRelocateRestaurantInventoryStocks,
  deleteRestaurantInventoryStock,
  parseInventoryImport,
  relocateRestaurantInventoryStock,
  updateRestaurantInventoryItem,
  type UpdateRestaurantInventoryItemPayload
} from './Api/Inventory.api';
import {
  useInventoryAddSession,
  useInventoryCategories,
  useLocationSuppliers,
  useSavedInventoryItems,
  useStorageLocations
} from './Hooks';
import { DEMO_SUPPLIERS } from './onboardingDemoData';
import type {
  CreateInventoryItemPayload,
  ImportedInventoryItem,
  InventoryItemCategory,
  InventoryItemDraft,
  LocationSupplier,
  OnboardingStep
} from './Types';
import {
  buildInventoryDraftLocalId,
  formatApiErrorMessage
} from './Utils/Inventory.utils';
import {
  dedupeImportedInventoryItems,
  draftInventoryItemKey,
  importedInventoryItemKey
} from './Utils/ImportDedupe.utils';
import { onboardingStepShellClass } from './Utils/onboardingLayout';

const STEP_META = [
  { id: 'restaurant', label: 'Restaurant' },
  { id: 'upload', label: 'Import' },
  { id: 'items', label: 'Items' },
  { id: 'storage', label: 'Storage' },
  { id: 'suppliers', label: 'Suppliers' },
  { id: 'review', label: 'Review' },
  { id: 'par', label: 'Par levels' },
  { id: 'finish', label: 'Finish' }
] as const;

const TOTAL_STEPS = STEP_META.length;
const FINISH_STEP = TOTAL_STEPS;
const IMPORT_STEP = 2;
const ITEMS_STEP = 3;
const STORAGE_STEP = 4;

export const InventoryOnboardingPage = () => {
  const { notify } = useToast();
  const { accessToken } = useAuth();
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const linkId = searchParams.get('link');

  const {
    sessionToken,
    branch,
    restaurantName,
    defaultLocationId,
    isLive,
    loading: sessionLoading,
    error: sessionError
  } = useInventoryAddSession(linkId, accessToken);

  const {
    items: savedItems,
    loading: savedItemsLoading,
    setItems: setSavedItems
  } = useSavedInventoryItems(sessionToken, branch.id, isLive);

  const {
    suppliers: liveSuppliers,
    adding: addingSupplier,
    createOutOfNetworkSupplier
  } = useLocationSuppliers(sessionToken, branch.id, isLive);

  const {
    categories: apiCategories,
    creating: creatingCategory,
    createCategory
  } = useInventoryCategories(sessionToken, branch.id, isLive);

  const {
    locations: storageLocations,
    loading: storageLocationsLoading,
    creating: creatingStorageLocation,
    createLocation: createStorageLocation,
    reload: reloadStorageLocations
  } = useStorageLocations(sessionToken, branch.id, isLive);

  // Invite already knows the restaurant — start on Import (step 2).
  const [currentStep, setCurrentStep] = useState(IMPORT_STEP);
  const [maxReachedStep, setMaxReachedStep] = useState(IMPORT_STEP);
  const [demoItems, setDemoItems] = useState<InventoryItemDraft[]>([]);
  const [importedDrafts, setImportedDrafts] = useState<InventoryItemDraft[]>(
    []
  );
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [importParsing, setImportParsing] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [demoCategories, setDemoCategories] = useState<InventoryItemCategory[]>(
    []
  );
  const [demoSuppliers, setDemoSuppliers] =
    useState<LocationSupplier[]>(DEMO_SUPPLIERS);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingStockId, setUpdatingStockId] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [creatingDemoCategory, setCreatingDemoCategory] = useState(false);
  const [preferredSupplierId, setPreferredSupplierId] = useState<string | null>(
    null
  );
  const [preferredCategoryId, setPreferredCategoryId] = useState<string | null>(
    null
  );

  const suppliers = isLive ? liveSuppliers : demoSuppliers;
  const categories = isLive ? apiCategories : demoCategories;
  const storageLocationId = defaultLocationId;

  const liveDrafts = useMemo(
    () => (savedItems ?? []).filter(item => item.name.trim()),
    [savedItems]
  );

  const drafts = useMemo(() => {
    const base = isLive ? liveDrafts : demoItems;
    if (importedDrafts.length === 0) return base;
    // Always show newly imported/pending rows first, then existing inventory.
    // Do not hide imports that share a name with an already-saved item.
    const pendingIds = new Set(importedDrafts.map(d => d.localId));
    const existingOnly = base.filter(d => !pendingIds.has(d.localId));
    return [...importedDrafts, ...existingOnly];
  }, [demoItems, importedDrafts, isLive, liveDrafts]);

  const existingInventoryCount = useMemo(
    () =>
      drafts.filter(d => Boolean(d.restaurantInventoryItemId) && d.name.trim())
        .length,
    [drafts]
  );

  const listedItems = useMemo(
    () => drafts.map(d => draftToListedItem(d, categories, suppliers)),
    [categories, drafts, suppliers]
  );

  const steps: OnboardingStep[] = useMemo(
    () =>
      STEP_META.map((meta, index) => {
        const stepNum = index + 1;
        let status: OnboardingStep['status'] = 'upcoming';
        if (stepNum < currentStep) status = 'done';
        else if (stepNum === currentStep) status = 'active';
        return {
          id: meta.id as OnboardingStep['id'],
          label: meta.label,
          status
        };
      }),
    [currentStep]
  );

  const setupPath = linkId
    ? `web.gioallday.com/inventory/add?link=${linkId}`
    : null;

  const goToStep = useCallback((step: number) => {
    const clamped = Math.min(Math.max(step, 1), TOTAL_STEPS);
    setCurrentStep(clamped);
    setMaxReachedStep(prev => Math.max(prev, clamped));
  }, []);

  const nextStep = useCallback(() => {
    goToStep(Math.min(currentStep + 1, TOTAL_STEPS));
  }, [currentStep, goToStep]);

  const prevStep = useCallback(() => {
    goToStep(Math.max(currentStep - 1, 1));
  }, [currentStep, goToStep]);

  const patchLocalDraft = useCallback(
    (localId: string, patch: Partial<InventoryItemDraft>) => {
      setImportedDrafts(prev =>
        prev.map(item =>
          item.localId === localId ? { ...item, ...patch } : item
        )
      );
      if (isLive) {
        setSavedItems(prev =>
          (prev ?? []).map(item =>
            item.localId === localId ? { ...item, ...patch } : item
          )
        );
      } else {
        setDemoItems(prev =>
          prev.map(item =>
            item.localId === localId ? { ...item, ...patch } : item
          )
        );
      }
    },
    [isLive, setSavedItems]
  );

  const importedItemToDraft = useCallback(
    (row: ImportedInventoryItem): InventoryItemDraft => {
      const localId = `import-${crypto.randomUUID()}`;
      const { item } = row;
      return {
        localId,
        restaurantInventoryItemId: null,
        stockId: null,
        locationId:
          item.locationId ||
          (isLive ? storageLocationId : 'demo-location'),
        name: item.name,
        unit: item.unit?.trim() || 'unit',
        unitMultiplier:
          item.unitMultiplier != null ? String(item.unitMultiplier) : '',
        baseUnit: item.baseUnit ?? '',
        quantityOnHand:
          item.quantityOnHand != null ? String(item.quantityOnHand) : '',
        minQuantity:
          item.minQuantity != null ? String(item.minQuantity) : '0',
        minOrderQty:
          item.minOrderQty != null && Number(item.minOrderQty) > 0
            ? String(item.minOrderQty)
            : '',
        note: row.categoryName?.trim() || item.note?.trim() || '',
        categorySuggested: Boolean(row.categorySuggested),
        imagePreview: null,
        imageName: null,
        imageFile: null,
        imageUrl: item.imageUrl ?? null,
        supplierUserId: item.supplierUserId ?? ''
      };
    },
    [isLive, storageLocationId]
  );

  const handleCreateCategory = useCallback(
    async (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) {
        throw new Error('Category name is required');
      }

      if (isLive) {
        const existing = categories.find(
          c => c.name.trim().toLowerCase() === trimmed.toLowerCase()
        );
        if (existing) {
          setPreferredCategoryId(existing.id);
          return existing;
        }
        const created = await createCategory(trimmed);
        setPreferredCategoryId(created.id);
        return created;
      }
      setCreatingDemoCategory(true);
      try {
        const existing = demoCategories.find(
          c => c.name.toLowerCase() === trimmed.toLowerCase()
        );
        if (existing) {
          setPreferredCategoryId(existing.id);
          return existing;
        }
        const created = {
          id: `demo-cat-${crypto.randomUUID()}`,
          name: trimmed,
          sortOrder: demoCategories.length
        };
        setDemoCategories(prev => [...prev, created]);
        setPreferredCategoryId(created.id);
        return created;
      } finally {
        setCreatingDemoCategory(false);
      }
    },
    [categories, createCategory, demoCategories, isLive]
  );

  const handleAddItem = useCallback(
    async (payload: CreateInventoryItemPayload) => {
      // Keep new rows local until "Save and continue" — do not hit the API yet.
      const localId = `${isLive ? 'pending' : 'demo'}-${crypto.randomUUID()}`;
      const categoryName =
        categories.find(c => c.id === payload.categoryId)?.name ??
        payload.note ??
        '';
      const draft: InventoryItemDraft = {
        localId,
        restaurantInventoryItemId: null,
        stockId: null,
        locationId: isLive
          ? payload.locationId || storageLocationId
          : 'demo-location',
        name: payload.name,
        unit: payload.unit,
        unitMultiplier:
          payload.unitMultiplier != null ? String(payload.unitMultiplier) : '',
        baseUnit: payload.baseUnit ?? '',
        quantityOnHand:
          payload.quantityOnHand != null ? String(payload.quantityOnHand) : '',
        minQuantity:
          payload.minQuantity != null ? String(payload.minQuantity) : '',
        minOrderQty:
          payload.minOrderQty != null && payload.minOrderQty > 0
            ? String(payload.minOrderQty)
            : '',
        note: categoryName,
        categorySuggested: false,
        imagePreview: null,
        imageName: null,
        imageFile: null,
        imageUrl: payload.imageUrl ?? null,
        supplierUserId: payload.supplierUserId ?? ''
      };

      if (isLive) {
        setImportedDrafts(prev => [draft, ...prev]);
      } else {
        setDemoItems(prev => [draft, ...prev]);
      }
      notify('success', `Added ${payload.name}`);
    },
    [categories, isLive, notify, storageLocationId]
  );

  const handleDeleteItem = useCallback(
    async (item: ReturnType<typeof draftToListedItem>) => {
      const isPendingLocal =
        !item.restaurantInventoryItemId &&
        (item.localId.startsWith('import-') ||
          item.localId.startsWith('pending-') ||
          item.localId.startsWith('demo-'));

      if (isPendingLocal) {
        setImportedDrafts(prev => prev.filter(d => d.localId !== item.localId));
        setDemoItems(prev => prev.filter(d => d.localId !== item.localId));
        notify('success', `Removed ${item.name}`);
        return;
      }

      if (!isLive) {
        setDemoItems(prev => prev.filter(d => d.localId !== item.localId));
        notify('success', `Removed ${item.name}`);
        return;
      }

      if (!sessionToken || !item.stockId) {
        notify('error', 'Cannot remove this item yet');
        return;
      }

      setDeletingId(item.localId);
      try {
        await deleteRestaurantInventoryStock(item.stockId, {
          accessToken: sessionToken,
          branchId: branch.id
        });
        // Delete is scoped to one stock row, so drop only that row — an item
        // stocked in another position must stay listed.
        const deletedStockId = item.stockId;
        setSavedItems(prev =>
          (prev ?? []).filter(d =>
            d.stockId
              ? d.stockId !== deletedStockId
              : d.localId !== item.localId
          )
        );
        await reloadStorageLocations();
        notify('success', `Removed ${item.name}`);
      } catch (err) {
        notify(
          'error',
          formatApiErrorMessage(err, 'Failed to remove item. Try again.')
        );
      } finally {
        setDeletingId(null);
      }
    },
    [
      branch.id,
      isLive,
      notify,
      reloadStorageLocations,
      sessionToken,
      setSavedItems
    ]
  );

  const handleUpdateQuantity = useCallback(
    async (
      item: ReturnType<typeof draftToListedItem>,
      quantityOnHand: number
    ) => {
      const nextQuantity = Math.max(0, quantityOnHand);
      setUpdatingStockId(item.localId);
      try {
        if (isLive) {
          if (!item.restaurantInventoryItemId) {
            // Unsaved draft — only update local state.
            patchLocalDraft(item.localId, {
              quantityOnHand: String(nextQuantity)
            });
            return;
          }
          if (!sessionToken) {
            throw new Error('Item is not saved yet');
          }
          const inventoryLocationId =
            item.locationId?.trim() || storageLocationId;
          if (!inventoryLocationId) {
            throw new Error(
              'Assign an inventory position before updating stock'
            );
          }
          await updateRestaurantInventoryItem(
            item.restaurantInventoryItemId,
            {
              locationId: inventoryLocationId,
              quantityOnHand: nextQuantity
            },
            { accessToken: sessionToken, branchId: branch.id }
          );
        }
        patchLocalDraft(item.localId, {
          quantityOnHand: String(nextQuantity)
        });
      } catch (err) {
        notify(
          'error',
          formatApiErrorMessage(err, 'Failed to update stock quantity')
        );
      } finally {
        setUpdatingStockId(null);
      }
    },
    [
      branch.id,
      isLive,
      notify,
      patchLocalDraft,
      sessionToken,
      storageLocationId
    ]
  );

  const handleUpdateItem = useCallback(
    async (
      item: ReturnType<typeof draftToListedItem>,
      payload: UpdateRestaurantInventoryItemPayload
    ) => {
      setUpdatingStockId(item.localId);
      try {
        if (isLive && item.restaurantInventoryItemId) {
          if (!sessionToken) {
            throw new Error('Item is not saved yet');
          }
          const inventoryLocationId =
            payload.locationId?.trim() ||
            item.locationId?.trim() ||
            storageLocationId;
          if (
            (payload.quantityOnHand != null || payload.minQuantity != null) &&
            !inventoryLocationId
          ) {
            throw new Error(
              'Assign an inventory position before editing stock'
            );
          }

          await updateRestaurantInventoryItem(
            item.restaurantInventoryItemId,
            {
              ...payload,
              ...(inventoryLocationId
                ? { locationId: inventoryLocationId }
                : {})
            },
            { accessToken: sessionToken, branchId: branch.id }
          );
        }

        const nextCategoryName = payload.clearCategory
          ? ''
          : payload.categoryId
            ? (categories.find(c => c.id === payload.categoryId)?.name ?? '')
            : undefined;

        const draftPatch: Partial<InventoryItemDraft> = {
          name: payload.name?.trim() || item.name,
          unit: payload.unit?.trim() || item.unit
        };

        if (payload.quantityOnHand != null) {
          draftPatch.quantityOnHand = String(payload.quantityOnHand);
        }
        if (payload.minQuantity != null) {
          draftPatch.minQuantity = String(payload.minQuantity);
        }
        if (payload.minOrderQty != null) {
          draftPatch.minOrderQty =
            payload.minOrderQty > 0 ? String(payload.minOrderQty) : '';
        }
        if (payload.unitMultiplier != null || payload.baseUnit != null) {
          draftPatch.unitMultiplier =
            payload.unitMultiplier != null
              ? String(payload.unitMultiplier)
              : '';
          draftPatch.baseUnit = payload.baseUnit ?? '';
        }
        if (nextCategoryName !== undefined) {
          draftPatch.note = nextCategoryName;
          draftPatch.categorySuggested = false;
        }
        if (payload.supplierUserId !== undefined) {
          draftPatch.supplierUserId = payload.supplierUserId;
        }

        patchLocalDraft(item.localId, draftPatch);
        notify('success', `Updated ${draftPatch.name}`);
      } catch (err) {
        notify('error', formatApiErrorMessage(err, 'Failed to update item'));
        throw err;
      } finally {
        setUpdatingStockId(null);
      }
    },
    [
      branch.id,
      categories,
      isLive,
      notify,
      patchLocalDraft,
      sessionToken,
      storageLocationId
    ]
  );

  const handleAssignLocation = useCallback(
    async (
      item: ReturnType<typeof draftToListedItem>,
      inventoryLocationId: string
    ) => {
      setUpdatingStockId(item.localId);
      try {
        if (isLive && item.stockId && item.restaurantInventoryItemId) {
          if (!sessionToken) {
            throw new Error('Item stock is not saved yet');
          }
          const relocated = await relocateRestaurantInventoryStock(
            item.stockId,
            inventoryLocationId,
            { accessToken: sessionToken, branchId: branch.id }
          );
          patchLocalDraft(item.localId, {
            stockId: relocated.stockId,
            locationId: relocated.locationId,
            quantityOnHand: String(relocated.quantityOnHand),
            locationSuggested: false,
            locationSuggestedPath: null
          });
          await reloadStorageLocations();
        } else {
          // Unsaved draft — local only until Save and continue.
          patchLocalDraft(item.localId, {
            locationId: inventoryLocationId,
            locationSuggested: false,
            locationSuggestedPath: null
          });
        }
        notify('success', `Position assigned for ${item.name}`);
      } catch (err) {
        notify(
          'error',
          formatApiErrorMessage(err, 'Failed to assign inventory position')
        );
      } finally {
        setUpdatingStockId(null);
      }
    },
    [
      branch.id,
      isLive,
      notify,
      patchLocalDraft,
      reloadStorageLocations,
      sessionToken
    ]
  );

  const handleBulkAssignSupplier = useCallback(
    async (
      items: Array<ReturnType<typeof draftToListedItem>>,
      supplierUserId: string
    ) => {
      const selectedLocalIds = new Set(items.map(item => item.localId));
      try {
        if (isLive) {
          if (!sessionToken)
            throw new Error('Inventory session is unavailable');
          const restaurantInventoryItemIds = [
            ...new Set(
              items
                .map(item => item.restaurantInventoryItemId?.trim())
                .filter((id): id is string => Boolean(id))
            )
          ];
          if (restaurantInventoryItemIds.length) {
            await bulkAssignRestaurantInventorySupplier(
              restaurantInventoryItemIds,
              supplierUserId,
              { accessToken: sessionToken, branchId: branch.id }
            );
          }
          // Update local drafts (includes unsaved pending/import rows).
          setImportedDrafts(prev =>
            prev.map(item =>
              selectedLocalIds.has(item.localId)
                ? { ...item, supplierUserId }
                : item
            )
          );
          setSavedItems(prev =>
            (prev ?? []).map(item =>
              selectedLocalIds.has(item.localId)
                ? { ...item, supplierUserId }
                : item
            )
          );
        } else {
          setDemoItems(previous =>
            previous.map(item =>
              selectedLocalIds.has(item.localId)
                ? { ...item, supplierUserId }
                : item
            )
          );
        }
        notify(
          'success',
          `Supplier assigned to ${items.length} selected items`
        );
      } catch (error) {
        notify(
          'error',
          formatApiErrorMessage(error, 'Failed to assign supplier')
        );
        throw error;
      }
    },
    [branch.id, isLive, notify, sessionToken, setSavedItems]
  );

  const handleBulkAssignLocation = useCallback(
    async (
      items: Array<ReturnType<typeof draftToListedItem>>,
      inventoryLocationId: string
    ) => {
      const selectedLocalIds = new Set(items.map(item => item.localId));
      try {
        if (isLive) {
          if (!sessionToken)
            throw new Error('Inventory session is unavailable');
          const stockIds = [
            ...new Set(
              items
                .map(item => item.stockId?.trim())
                .filter((id): id is string => Boolean(id))
            )
          ];
          if (stockIds.length !== items.length) {
            throw new Error(
              'One or more selected stock rows are not saved yet'
            );
          }

          const relocated = await bulkRelocateRestaurantInventoryStocks(
            stockIds,
            inventoryLocationId,
            { accessToken: sessionToken, branchId: branch.id }
          );
          const resultBySource = new Map(
            relocated.map(result => [result.sourceStockId, result])
          );
          const resultByDestination = new Map(
            relocated.map(result => [result.stockId, result])
          );

          setSavedItems(previous => {
            const updated = (previous ?? []).map(item => {
              const sourceResult = item.stockId
                ? resultBySource.get(item.stockId)
                : undefined;
              const destinationResult = item.stockId
                ? resultByDestination.get(item.stockId)
                : undefined;
              const result = sourceResult ?? destinationResult;
              if (!result) return item;

              const stockId = sourceResult?.stockId ?? item.stockId;
              if (!stockId || !item.restaurantInventoryItemId) return item;
              return {
                ...item,
                localId: buildInventoryDraftLocalId(
                  item.restaurantInventoryItemId,
                  stockId
                ),
                stockId,
                locationId: result.locationId,
                quantityOnHand: String(result.quantityOnHand),
                locationSuggested: false,
                locationSuggestedPath: null
              };
            });

            return [
              ...new Map(updated.map(item => [item.localId, item])).values()
            ];
          });
          await reloadStorageLocations();
        } else {
          setDemoItems(previous =>
            previous.map(item =>
              selectedLocalIds.has(item.localId)
                ? {
                    ...item,
                    locationId: inventoryLocationId,
                    locationSuggested: false,
                    locationSuggestedPath: null
                  }
                : item
            )
          );
          setImportedDrafts(previous =>
            previous.map(item =>
              selectedLocalIds.has(item.localId)
                ? {
                    ...item,
                    locationId: inventoryLocationId,
                    locationSuggested: false,
                    locationSuggestedPath: null
                  }
                : item
            )
          );
        }
        notify('success', `Storage assigned to ${items.length} selected items`);
      } catch (error) {
        notify(
          'error',
          formatApiErrorMessage(error, 'Failed to assign storage')
        );
        throw error;
      }
    },
    [
      branch.id,
      isLive,
      notify,
      reloadStorageLocations,
      sessionToken,
      setSavedItems
    ]
  );

  const handleStorageNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  const handleAssignCategory = useCallback(
    async (
      item: ReturnType<typeof draftToListedItem>,
      category: InventoryItemCategory
    ) => {
      setAssigningId(item.localId);
      try {
        if (isLive) {
          if (!sessionToken || !item.restaurantInventoryItemId) {
            throw new Error('Item is not saved yet');
          }
          await updateRestaurantInventoryItem(
            item.restaurantInventoryItemId,
            { categoryId: category.id },
            { accessToken: sessionToken, branchId: branch.id }
          );
        }
        patchLocalDraft(item.localId, {
          note: category.name,
          categorySuggested: false
        });
      } catch (err) {
        notify(
          'error',
          formatApiErrorMessage(err, 'Failed to assign category')
        );
      } finally {
        setAssigningId(null);
      }
    },
    [branch.id, isLive, notify, patchLocalDraft, sessionToken]
  );

  const handleUpdatePar = useCallback(
    async (item: ReturnType<typeof draftToListedItem>, parLevel: number) => {
      if (isLive) {
        if (!sessionToken || !item.restaurantInventoryItemId) {
          notify('error', 'Item is not saved yet');
          return;
        }
        try {
          await updateRestaurantInventoryItem(
            item.restaurantInventoryItemId,
            {
              minQuantity: parLevel,
              ...(storageLocationId ? { locationId: storageLocationId } : {})
            },
            { accessToken: sessionToken, branchId: branch.id }
          );
        } catch (err) {
          notify(
            'error',
            formatApiErrorMessage(err, 'Failed to update par level')
          );
          return;
        }
      }
      patchLocalDraft(item.localId, { minQuantity: String(parLevel) });
    },
    [
      branch.id,
      isLive,
      notify,
      patchLocalDraft,
      sessionToken,
      storageLocationId
    ]
  );

  const applyImportParseResult = useCallback(
    (result: {
      items: ImportedInventoryItem[];
      fileName?: string;
      warnings?: string[];
    }): ImportedInventoryItem[] => {
      const { items, mergedCount } = dedupeImportedInventoryItems(result.items);
      const normalized = items.map(row => ({
        ...row,
        item: {
          ...row.item,
          name: row.item.name,
          minQuantity:
            row.item.minQuantity != null &&
            Number.isFinite(Number(row.item.minQuantity)) &&
            Number(row.item.minQuantity) >= 0
              ? Number(row.item.minQuantity)
              : 0
        }
      }));
      const warnings = [...(result.warnings ?? [])];
      if (mergedCount > 0) {
        warnings.push(
          mergedCount === 1
            ? '1 duplicate row was merged (same name and unit).'
            : `${mergedCount} duplicate rows were merged (same name and unit).`
        );
      }
      setImportWarnings(warnings);
      if (normalized.length === 0) {
        setImportError(
          'No inventory items found. Try a clearer list or spreadsheet.'
        );
      }
      return normalized;
    },
    []
  );

  const commitImportAndGoToItems = useCallback(
    (importedItems: ImportedInventoryItem[]) => {
      if (importedItems.length === 0) return;

      const { items: uniqueImport } =
        dedupeImportedInventoryItems(importedItems);

      const existingInventory = isLive ? liveDrafts : demoItems;
      const existingKeys = new Set(
        existingInventory.map(d => draftInventoryItemKey(d.name, d.unit))
      );

      const fresh = uniqueImport.filter(
        row => !existingKeys.has(importedInventoryItemKey(row))
      );
      const skippedExisting = uniqueImport.length - fresh.length;

      const draftsFromImport = fresh.map(importedItemToDraft);
      setImportedDrafts(draftsFromImport);

      const parts: string[] = [];
      if (draftsFromImport.length > 0) {
        parts.push(
          `${draftsFromImport.length} new item${draftsFromImport.length === 1 ? '' : 's'} added`
        );
      }
      if (skippedExisting > 0) {
        parts.push(`${skippedExisting} already in inventory skipped`);
      }
      notify(
        'success',
        parts.length > 0
          ? parts.join(' · ')
          : 'No new items to add — everything was already in inventory'
      );
      goToStep(ITEMS_STEP);
    },
    [demoItems, goToStep, importedItemToDraft, isLive, liveDrafts, notify]
  );

  const handleParseImportFile = useCallback(
    async (file: File) => {
      setImportParsing(true);
      setImportError(null);
      try {
        if (isLive && sessionToken) {
          const result = await parseInventoryImport(
            { file },
            sessionToken
          );
          const items = applyImportParseResult(result);
          if (items.length > 0) {
            commitImportAndGoToItems(items);
          }
          return;
        }

        // Preview mode: local spreadsheet parse (no backend / LLM).
        const {
          parseInventoryExcelFile,
          getInventorySpreadsheetValidationError
        } = await import('./Utils/InventoryExcel.utils');
        const { toTitleCaseName } = await import('./Api/Inventory.api');
        const validationError = getInventorySpreadsheetValidationError(file);
        if (validationError) {
          throw new Error(validationError);
        }
        const parsed = await parseInventoryExcelFile(file);
        const rawItems: ImportedInventoryItem[] = parsed.rows.map(row => {
          const hasConversion =
            Boolean(row.packageQuantity?.trim()) &&
            Boolean(row.packageUnit?.trim()) &&
            Number(row.packageQuantity) > 0;
          const unit = row.purchasedAs?.trim() || 'unit';
          const parNum = Number(row.parLevel);
          const minQuantity =
            row.parLevel?.trim() && Number.isFinite(parNum) && parNum >= 0
              ? parNum
              : 0;
          return {
            item: {
              name: toTitleCaseName(row.name),
              unit,
              minQuantity,
              ...(hasConversion
                ? {
                    unitMultiplier: Number(row.packageQuantity),
                    baseUnit: row.packageUnit
                  }
                : {})
            },
            categoryName: row.category || null,
            categorySuggested: false,
            confidence: row.purchasedAs ? 'high' : 'low',
            rawSnippet: null,
            needsReview: row.needsReview || !row.purchasedAs
          };
        });
        const invalidPar = parsed.issues.filter(i =>
          /par level|par_level|min quantity/i.test(i.message)
        ).length;
        const otherIssueCount = Math.max(0, parsed.issues.length - invalidPar);
        const humanWarnings: string[] = [];
        if (invalidPar > 0) {
          humanWarnings.push(
            invalidPar === 1
              ? '1 item had a par level that wasn’t a number, so it was set to 0. You can change par levels later.'
              : `${invalidPar} items had par levels that weren’t numbers, so those values were set to 0. You can change par levels later.`
          );
        }
        if (otherIssueCount > 0) {
          humanWarnings.push(
            otherIssueCount === 1
              ? '1 other field couldn’t be read cleanly and was skipped.'
              : `${otherIssueCount} other fields couldn’t be read cleanly and were skipped.`
          );
        }
        const items = applyImportParseResult({
          items: rawItems,
          fileName: file.name,
          warnings: humanWarnings
        });
        if (items.length > 0) {
          commitImportAndGoToItems(items);
        }
      } catch (err) {
        setImportWarnings([]);
        setImportError(
          formatApiErrorMessage(err, 'Failed to read this file. Try again.')
        );
      } finally {
        setImportParsing(false);
      }
    },
    [
      applyImportParseResult,
      commitImportAndGoToItems,
      isLive,
      sessionToken
    ]
  );

  const handleParseImportText = useCallback(
    async (text: string) => {
      setImportParsing(true);
      setImportError(null);
      try {
        if (!isLive || !sessionToken) {
          throw new Error(
            'Open this page from your inventory invite link to extract pasted text.'
          );
        }
        const result = await parseInventoryImport({ text }, sessionToken);
        const items = applyImportParseResult(result);
        if (items.length > 0) {
          commitImportAndGoToItems(items);
        }
      } catch (err) {
        setImportWarnings([]);
        setImportError(
          formatApiErrorMessage(err, 'Failed to extract inventory from text.')
        );
      } finally {
        setImportParsing(false);
      }
    },
    [
      applyImportParseResult,
      commitImportAndGoToItems,
      isLive,
      sessionToken
    ]
  );

  const handleSkipImport = useCallback(() => {
    setImportedDrafts([]);
    goToStep(ITEMS_STEP);
  }, [goToStep]);

  const pendingUnsavedDrafts = useMemo(
    () =>
      drafts.filter(
        d =>
          d.name.trim() &&
          !d.restaurantInventoryItemId &&
          (d.localId.startsWith('import-') ||
            d.localId.startsWith('pending-') ||
            d.localId.startsWith('demo-'))
      ),
    [drafts]
  );

  const handleDoneAddingItems = useCallback(async () => {
    const unsaved = pendingUnsavedDrafts;
    if (!isLive) {
      notify(
        'success',
        unsaved.length > 0
          ? `${unsaved.length} item${unsaved.length === 1 ? '' : 's'} ready — set storage next`
          : 'Continuing — you can add items anytime'
      );
      goToStep(STORAGE_STEP);
      return;
    }

    if (unsaved.length === 0) {
      goToStep(STORAGE_STEP);
      return;
    }

    if (!sessionToken || !storageLocationId) {
      notify(
        'error',
        'Open this page from your inventory invite link to save items'
      );
      return;
    }

    setSubmitting(true);
    try {
      // Resolve category labels on drafts (stored in `note`) → categoryId.
      // Create any AI/import categories that do not exist yet.
      const categoryIdByName = new Map<string, string>();
      for (const c of categories) {
        categoryIdByName.set(c.name.trim().toLowerCase(), c.id);
      }
      const namesToEnsure = [
        ...new Set(
          unsaved
            .map(d => d.note.trim())
            .filter((n): n is string => Boolean(n))
        )
      ];
      for (const categoryName of namesToEnsure) {
        const key = categoryName.toLowerCase();
        if (categoryIdByName.has(key)) continue;
        const created = await handleCreateCategory(categoryName);
        categoryIdByName.set(key, created.id);
      }

      const payloadItems = unsaved.map(d => {
        const categoryKey = d.note.trim().toLowerCase();
        const categoryId = categoryKey
          ? categoryIdByName.get(categoryKey)
          : undefined;
        return {
          name: d.name.trim(),
          unit: d.unit.trim() || 'unit',
          locationId: d.locationId?.trim() || storageLocationId,
          ...(d.quantityOnHand.trim()
            ? { quantityOnHand: Number(d.quantityOnHand) }
            : {}),
          ...(d.minQuantity.trim()
            ? { minQuantity: Number(d.minQuantity) }
            : {}),
          ...(d.minOrderQty.trim()
            ? { minOrderQty: Number(d.minOrderQty) }
            : {}),
          ...(d.unitMultiplier.trim() && d.baseUnit.trim()
            ? {
                unitMultiplier: Number(d.unitMultiplier),
                baseUnit: d.baseUnit.trim()
              }
            : {}),
          ...(categoryId ? { categoryId } : {}),
          ...(d.imageUrl ? { imageUrl: d.imageUrl } : {}),
          ...(d.supplierUserId.trim()
            ? { supplierUserId: d.supplierUserId.trim() }
            : {})
        };
      });

      // API allows max 500 per request.
      const chunkSize = 500;
      const createdAll: Awaited<
        ReturnType<typeof bulkCreateRestaurantInventoryItems>
      > = [];
      for (let i = 0; i < payloadItems.length; i += chunkSize) {
        const chunk = payloadItems.slice(i, i + chunkSize);
        const created = await bulkCreateRestaurantInventoryItems(
          { items: chunk },
          { accessToken: sessionToken, branchId: branch.id }
        );
        createdAll.push(...created);
      }

      const savedDrafts: InventoryItemDraft[] = createdAll.map(
        (result, index) => {
          const source = unsaved[index];
          return {
            localId: buildInventoryDraftLocalId(
              result.restaurantInventoryItemId,
              result.stockId
            ),
            restaurantInventoryItemId: result.restaurantInventoryItemId,
            stockId: result.stockId,
            locationId: storageLocationId,
            name: result.name || source?.name || '',
            unit: result.unit || source?.unit || 'unit',
            unitMultiplier: source?.unitMultiplier ?? '',
            baseUnit: source?.baseUnit ?? '',
            quantityOnHand: source?.quantityOnHand ?? '',
            minQuantity: source?.minQuantity ?? '',
            minOrderQty: source?.minOrderQty ?? '',
            note: source?.note ?? '',
            categorySuggested: false,
            imagePreview: null,
            imageName: null,
            imageFile: null,
            imageUrl: source?.imageUrl ?? null,
            supplierUserId:
              result.assignedSupplierId ?? source?.supplierUserId ?? ''
          };
        }
      );

      setImportedDrafts([]);
      setSavedItems(prev => {
        const existing = (prev ?? []).filter(r => r.name.trim());
        return [...savedDrafts, ...existing];
      });
      await reloadStorageLocations();

      notify(
        'success',
        `Saved ${savedDrafts.length} item${savedDrafts.length === 1 ? '' : 's'} — set storage next`
      );
      goToStep(STORAGE_STEP);
    } catch (err) {
      notify(
        'error',
        formatApiErrorMessage(err, 'Failed to save items. Try again.')
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    branch.id,
    categories,
    goToStep,
    handleCreateCategory,
    isLive,
    notify,
    pendingUnsavedDrafts,
    reloadStorageLocations,
    sessionToken,
    setSavedItems,
    storageLocationId
  ]);

  const pageLoading = sessionLoading || (isLive && savedItemsLoading);

  if (pageLoading) {
    return (
      <div className="light flex min-h-[100dvh] items-center justify-center bg-[#fafafa]">
        <Loader2 className="h-5 w-5 animate-spin text-[#6f747c]" />
      </div>
    );
  }

  return (
    <div className="light flex min-h-[100dvh] bg-[#fafafa] text-[#171717]">
      <div className="hidden w-56 shrink-0 md:block">
        <div className="fixed inset-y-0 left-0 w-56">
          <OnboardingSidebar
            restaurantName={restaurantName}
            branchName={branch.name}
            steps={steps}
            maxReachedStep={maxReachedStep}
            itemCount={listedItems.length}
            setupPath={setupPath}
            onNavigate={goToStep}
          />
        </div>
      </div>

      <main className="relative flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12, ease: 'easeOut' }}
              className="mx-auto min-h-full w-full max-w-7xl p-8 md:p-14"
            >
              {sessionError && (
                <p className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-2 text-sm text-red-600">
                  {sessionError}
                </p>
              )}

              {!isLive && !sessionError && (
                <p className="mb-4 rounded-lg border border-amber-100 bg-amber-50 px-4 py-2 text-sm text-amber-700">
                  Preview mode — open an invite link with ?link=… to save live
                  inventory for that restaurant.
                </p>
              )}

              {isLive &&
                !storageLocationId &&
                (currentStep === ITEMS_STEP || currentStep === STORAGE_STEP) && (
                  <p className="mb-4 rounded-lg border border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">
                    No storage area is configured for this location yet, so
                    items cannot be saved.
                  </p>
                )}

              {currentStep === 1 && (
                <RestaurantStep
                  restaurantName={restaurantName}
                  branchName={branch.name}
                  branchAddress={branch.address}
                  onNext={nextStep}
                />
              )}

              {currentStep === IMPORT_STEP && (
                <ImportStep
                  parsing={importParsing}
                  error={importError}
                  onParseFile={handleParseImportFile}
                  onParseText={handleParseImportText}
                  onSkip={handleSkipImport}
                />
              )}

              {currentStep === ITEMS_STEP && (
                <AddItemsForm
                  categories={categories}
                  suppliers={suppliers}
                  items={listedItems}
                  defaultLocationId={
                    isLive ? storageLocationId : 'demo-location'
                  }
                  isLive={isLive}
                  submitting={submitting}
                  deletingId={deletingId}
                  updatingId={updatingStockId}
                  storageLocations={storageLocations}
                  storageLocationsLoading={storageLocationsLoading}
                  creatingStorageLocation={creatingStorageLocation}
                  preferredSupplierId={preferredSupplierId}
                  preferredCategoryId={preferredCategoryId}
                  statusNotice={
                    pendingUnsavedDrafts.length > 0 ||
                    existingInventoryCount > 0 ||
                    importWarnings.length > 0 ? (
                      <div className="space-y-2">
                        {pendingUnsavedDrafts.length > 0 ||
                        existingInventoryCount > 0 ? (
                          <p className="rounded-lg border border-[#e7e9ee] bg-white px-4 py-2.5 text-sm text-[#6f747c]">
                            {pendingUnsavedDrafts.length > 0 ? (
                              <>
                                <span className="font-medium text-[#171717]">
                                  {pendingUnsavedDrafts.length} new
                                </span>
                                {' from import / not saved yet'}
                              </>
                            ) : null}
                            {pendingUnsavedDrafts.length > 0 &&
                            existingInventoryCount > 0
                              ? ' · '
                              : null}
                            {existingInventoryCount > 0 ? (
                              <>
                                <span className="font-medium text-[#171717]">
                                  {existingInventoryCount} existing
                                </span>
                                {' already on this location'}
                              </>
                            ) : null}
                          </p>
                        ) : null}
                        {importWarnings.length > 0 ? (
                          <div className="space-y-1.5 rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                            {importWarnings.map((w, i) => (
                              <p key={`${i}-${w.slice(0, 32)}`}>{w}</p>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null
                  }
                  onAddItem={handleAddItem}
                  onDeleteItem={handleDeleteItem}
                  onUpdateQuantity={handleUpdateQuantity}
                  onUpdateItem={handleUpdateItem}
                  onAssignLocation={handleAssignLocation}
                  onCreateStorageLocation={createStorageLocation}
                  onEnsureCategory={handleCreateCategory}
                  onBack={prevStep}
                  onDone={handleDoneAddingItems}
                  doneLabel={
                    pendingUnsavedDrafts.length > 0
                      ? 'Save and continue'
                      : 'Continue to storage'
                  }
                  doneLoading={submitting}
                  onRequestAddSupplier={() => setAddSupplierOpen(true)}
                  onRequestAddCategory={() => setAddCategoryOpen(true)}
                />
              )}

              {currentStep === STORAGE_STEP && (
                <StorageStep
                  items={listedItems}
                  locations={storageLocations}
                  defaultLocationId={
                    isLive ? storageLocationId : 'demo-location'
                  }
                  locationsLoading={storageLocationsLoading}
                  creatingLocation={creatingStorageLocation}
                  onCreateLocation={createStorageLocation}
                  onBulkAssignLocation={handleBulkAssignLocation}
                  onNext={handleStorageNext}
                  onPrev={prevStep}
                />
              )}

              {currentStep === 5 && (
                <SuppliersStep
                  items={listedItems}
                  suppliers={suppliers}
                  onBulkAssignSupplier={handleBulkAssignSupplier}
                  onRequestAddSupplier={() => setAddSupplierOpen(true)}
                  onNext={nextStep}
                  onPrev={prevStep}
                />
              )}

              {currentStep === 6 && (
                <ReviewStep
                  items={listedItems}
                  categories={categories}
                  assigningId={assigningId}
                  onAssignCategory={handleAssignCategory}
                  onRequestAddCategory={() => setAddCategoryOpen(true)}
                  onNext={nextStep}
                  onPrev={prevStep}
                />
              )}

              {currentStep === 7 && (
                <ParLevelsStep
                  items={listedItems}
                  onUpdatePar={handleUpdatePar}
                  onNext={nextStep}
                  onPrev={prevStep}
                />
              )}

              {currentStep === FINISH_STEP && (
                <div className={onboardingStepShellClass}>
                  <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[#087f8c] text-white">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-semibold tracking-tight text-[#171717]">
                      You&apos;re all set.
                    </h2>
                    <p className="mt-1 text-sm text-[#6f747c]">
                      Inventory setup is complete
                      {listedItems.length > 0
                        ? ` — ${listedItems.length} item${listedItems.length !== 1 ? 's' : ''} on this location`
                        : ''}
                      . You can come back anytime with this invite link to add
                      more.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 border-t border-[#e7e9ee] pt-4">
                    <button
                      type="button"
                      onClick={() => goToStep(ITEMS_STEP)}
                      className="rounded-md bg-[#087f8c] px-3 py-2 text-sm font-semibold text-white transition hover:bg-[#066873]"
                    >
                      Add more items
                    </button>
                    <button
                      type="button"
                      onClick={prevStep}
                      className="rounded-md border border-[#e7e9ee] bg-white px-3 py-2 text-sm font-medium text-[#171717] hover:bg-[#f0f2f5]"
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      <AddCategoryModal
        open={addCategoryOpen}
        onOpenChange={setAddCategoryOpen}
        existingCategories={categories.map(c => c.name)}
        submitting={isLive ? creatingCategory : creatingDemoCategory}
        onAdd={async category => {
          await handleCreateCategory(category);
          notify('success', `Category “${category}” created`);
        }}
      />

      <OutOfNetworkSupplierModal
        open={addSupplierOpen}
        onOpenChange={setAddSupplierOpen}
        submitting={addingSupplier}
        onAdd={async payload => {
          if (isLive) {
            const created = await createOutOfNetworkSupplier(payload);
            setPreferredSupplierId(created.supplier_id);
          } else {
            const created: LocationSupplier = {
              supplier_id: `demo-sup-${crypto.randomUUID()}`,
              business_name: payload.business_name,
              network_type: 'out_of_network',
              email: payload.email ?? null,
              phone: payload.phone ?? null,
              contact_person_name: payload.contact_person_name ?? null,
              is_archived: false,
              active: true
            };
            setDemoSuppliers(prev => [...prev, created]);
            setPreferredSupplierId(created.supplier_id);
          }
          notify('success', `Added ${payload.business_name}`);
        }}
      />
    </div>
  );
};
